import { Grid2 as Grid, InputAdornment } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import IsMountedWrapper from '../../../utils/functions/isMountedWrapper';
import { ProductsStore } from '../../../utils/stores/ProductsStore';

// Inline replacements for the old makeStyles classes (kept as plain `sx`).
const availabilitySx = {
    sectionItem: {
        display: { xs: 'flex', sm: 'block' },
        flexDirection: { xs: 'column' },
        alignItems: { xs: 'center' },
        width: { sm: '90%', md: 'auto' },
    },
    selectFields: {
        width: { xs: '100%', sm: '90%' },
        '& .MuiOutlinedInput-root': { height: 54 },
    },
} as const;
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import { sum } from 'lodash';
import { BookingStore } from '../../../utils/stores/BookingStore';

interface CalendarDateSelProps {
    readonly isPackage?: boolean;
}

export default function CalendarDateSel({ isPackage }: CalendarDateSelProps) {
    const productController = useContext(ProductsStore);
    const bookingController = useContext(BookingStore);
    const isMounted = IsMountedWrapper();
    const [calendarOpen, setCalendarOpen] = useState(false);

    const handleDateChange = (date: Date | null) => {
        if (!date) return;
        const momentDate = moment(date);
        isPackage
            ? productController.setAvailability(
                  (prev) => ({
                      ...prev,
                      packageDepartureDate: momentDate.utcOffset(10).toISOString(true),
                  }),
                  isMounted
              )
            : productController.setAvailability(
                  (prev) => ({
                      ...prev,
                      departureDate: momentDate.utcOffset(10).toISOString(true),
                  }),
                  isMounted
              );
    };

    useEffect(() => {
        productController.getAvailability();
    }, [
        isPackage ? productController.availability.packageDepartureDate : productController.availability.departureDate,
    ]);

    useEffect(() => {
        productController.getAvailability();
        const dateValue = isPackage
            ? moment(productController.availability.packageDepartureDate).isAfter(
                  moment().add(productController.packageProduct.data.minimumNoticeMinutes, 'minutes')
              )
                ? productController.availability.packageDepartureDate
                : productController.availability.packageSessions.find(
                      (sesh) =>
                          moment(sesh.startTime).isAfter(
                              moment().add(productController.packageProduct.data.minimumNoticeMinutes, 'minutes')
                          ) &&
                          sesh.seatsRemaining >
                              sum(bookingController.packageBooking.data.quantities.map((q) => q.seatsUsed * q.value)) &&
                          sesh
                  )?.startTime
            : moment();
        handleDateChange(dateValue ? moment(dateValue).toDate() : null);
    }, []);

    const currentDate = isPackage
        ? moment(productController.availability.packageDepartureDate).utcOffset(10).toDate()
        : moment(productController.availability.departureDate).utcOffset(10).toDate();

    return (
        <Grid size={{ xs: 12 }} sx={availabilitySx.sectionItem}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    format="dd MMMM"
                    openTo="day"
                    open={calendarOpen}
                    onOpen={() => setCalendarOpen(true)}
                    onClose={() => setCalendarOpen(false)}
                    slotProps={{
                        textField: {
                            error:
                                isPackage &&
                                bookingController.customerErrors.packageDate ===
                                    'Both products cannot be on the same day',
                            helperText:
                                isPackage &&
                                bookingController.customerErrors.packageDate ===
                                    'Both products cannot be on the same day' &&
                                bookingController.customerErrors.packageDate,
                            sx: { ...availabilitySx.selectFields, cursor: 'pointer' },
                            style: { width: isPackage && '100%' },
                            onClick: () => setCalendarOpen(true),
                            InputProps: {
                                startAdornment: (
                                    <InputAdornment position="start" style={{ paddingRight: 10 }}>
                                        <TodayOutlinedIcon />
                                    </InputAdornment>
                                ),
                            },
                        },
                    }}
                    value={currentDate}
                    onChange={handleDateChange}
                    minDate={moment().toDate()}
                />
            </LocalizationProvider>
        </Grid>
    );
}
